import { useEffect, useState } from "react";
import { page } from "./theme";
import { CrtOverlay } from "./components/CrtOverlay";
import { MatrixRain } from "./components/MatrixRain";
import { CookieNotice } from "./components/CookieNotice";
import { ToastProvider } from "./components/Toast";
import { Landing } from "./pages/Landing";
import { Checkout } from "./pages/Checkout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Legal, type LegalDoc } from "./pages/Legal";
import { getMe, type Me } from "./lib/api";
import { DEFAULT_PERIOD, type Period } from "./lib/pricing";

type Route = "landing" | "checkout" | "login" | "dashboard";

const params = new URLSearchParams(window.location.search);
const PREVIEW = params.get("preview") === "1";
const CRT = params.get("crt") !== "0";

function legalFromPath(p: string): LegalDoc | null {
  if (p === "/terms") return "terms";
  if (p === "/privacy") return "privacy";
  if (p === "/imprint") return "imprint";
  return null;
}
const LEGAL = legalFromPath(window.location.pathname);

export default function App() {
  const [route, setRoute] = useState<Route>("landing");
  const [me, setMe] = useState<Me | null>(null);
  const [email, setEmail] = useState("");
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [booting, setBooting] = useState(!LEGAL);
  const [cameFromCheckout, setCameFromCheckout] = useState(false);

  // On load, resolve any existing session. Also honor Stripe's success redirect
  // (?checkout=success) by sending the user to login to claim their OTP.
  useEffect(() => {
    if (LEGAL) {
      setBooting(false);
      return;
    }
    (async () => {
      try {
        const m = await getMe();
        setMe(m);
        setRoute("dashboard");
      } catch {
        if (params.get("checkout") === "success") {
          setCameFromCheckout(true);
          setRoute("login");
        }
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  if (booting) {
    return (
      <div style={{ ...page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 30, height: 30, border: "3px solid #1b2a20", borderTopColor: "#36e07f", borderRadius: "50%", animation: "spin .8s linear infinite" }} />
      </div>
    );
  }

  if (LEGAL) {
    return (
      <div style={page}>
        <CrtOverlay on={CRT} />
        <Legal doc={LEGAL} />
        <CookieNotice />
      </div>
    );
  }

  return (
    <ToastProvider>
      <div style={page}>
        <CrtOverlay on={CRT} />
        <CookieNotice />
        {route === "landing" && <MatrixRain />}
        {route === "landing" && (
          <Landing
            onLogin={() => setRoute("login")}
            onSubscribe={(p) => { setPeriod(p); setRoute("checkout"); }}
          />
        )}
        {route === "checkout" && (
          <Checkout
            period={period}
            email={email}
            setEmail={setEmail}
            preview={PREVIEW}
            onBack={() => setRoute("landing")}
            onContinueToLogin={() => { setCameFromCheckout(true); setRoute("login"); }}
          />
        )}
        {route === "login" && (
          <Login
            email={email}
            setEmail={setEmail}
            initialEmail={cameFromCheckout}
            onBack={() => setRoute("landing")}
            onSuccess={(m) => { setMe(m); setRoute("dashboard"); }}
          />
        )}
        {route === "dashboard" && me && (
          <Dashboard me={me} onLogout={() => { setMe(null); setRoute("landing"); }} />
        )}
      </div>
    </ToastProvider>
  );
}
